import React, { useState } from 'react';
import { FolderPlus, Upload, Trash2, Eye, ImageIcon, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ImageWithFallback } from '@/components/ui/image-with-fallback';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { FolderCard } from './FolderCard';
import { FolderBreadcrumb } from './FolderBreadcrumb';
import {
  useContentFolders,
  useCreateContentFolder,
  useDeleteContentFolder,
} from '../../hooks/useContentFolders';
import {
  useContentImages,
  useUploadContentImage,
  useDeleteContentImage,
  useContentImageUrl,
} from '../../hooks/useContentImages';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface ContentGallerySectionProps {
  campaignId: string;
}

export const ContentGallerySection: React.FC<ContentGallerySectionProps> = ({ campaignId }) => {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { data: folders = [], isLoading: foldersLoading } = useContentFolders(campaignId);
  const { data: images = [], isLoading: imagesLoading } = useContentImages(campaignId);
  const createFolderMutation = useCreateContentFolder();
  const deleteFolderMutation = useDeleteContentFolder();
  const uploadImageMutation = useUploadContentImage();
  const deleteImageMutation = useDeleteContentImage();

  const [newFolderName, setNewFolderName] = useState('');
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [currentViewFolder, setCurrentViewFolder] = useState<{ id: string; name: string } | null>(null);

  // Show authentication warning if not logged in
  if (!user) {
    return (
      <div className="p-6 space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please log in to access the content gallery.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  // Show organization warning if no organization selected
  if (!currentOrganization) {
    return (
      <div className="p-6 space-y-6">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Please select an organization to access the content gallery.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      await createFolderMutation.mutateAsync({
        campaignId,
        name: newFolderName.trim(),
        organizationId: currentOrganization.id,
      });

      setNewFolderName('');
      setCreateFolderOpen(false);
    } catch (error) {
      console.error('Error creating folder:', error);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!file.type.startsWith('image/')) {
        toast({
          title: 'Invalid file type',
          description: `${file.name} is not an image file`,
          variant: 'destructive',
        });
        continue;
      }

      try {
        await uploadImageMutation.mutateAsync({
          file,
          campaignId,
          organizationId: currentOrganization.id,
          folderId: selectedFolderId || undefined,
        });
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
      }
    }

    setUploadDialogOpen(false);
    event.target.value = '';
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (window.confirm('Are you sure you want to delete this folder? All images in this folder will also be deleted.')) {
      try {
        await deleteFolderMutation.mutateAsync({ folderId, campaignId });
      } catch (error) {
        console.error('Error deleting folder:', error);
      }
    }
  };

  const handleDeleteImage = async (imageId: string, filePath: string) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        await deleteImageMutation.mutateAsync({ imageId, filePath, campaignId });
      } catch (error) {
        console.error('Error deleting image:', error);
      }
    }
  };

  const getImagesInFolder = (folderId: string | null) => {
    return images.filter((image) => image.folder_id === folderId);
  };

  const getFolderImageCount = (folderId: string) => {
    return getImagesInFolder(folderId).length;
  };

  const getRandomFolderPreview = async (folderId: string) => {
    const folderImages = getImagesInFolder(folderId);
    if (folderImages.length === 0) return undefined;

    try {
      const randomImage = folderImages[0];
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return undefined;

      const { data, error } = await supabase.storage
        .from('campaign-content')
        .createSignedUrl(randomImage.file_path, 3600);

      if (error) throw error;
      return data.signedUrl;
    } catch (error) {
      console.error('Error getting folder preview:', error);
      return undefined;
    }
  };

  const handleFolderClick = (folder: { id: string; name: string }) => {
    setCurrentViewFolder(folder);
  };

  const handleBackToFolders = () => {
    setCurrentViewFolder(null);
  };

  if (foldersLoading || imagesLoading) {
    return <LoadingSkeleton />;
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-[20px] font-bold font-['Hanken_Grotesk',sans-serif]">Content Gallery</h3>
        <div className="flex gap-2">
          <Dialog open={createFolderOpen} onOpenChange={setCreateFolderOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="flex items-center gap-2">
                <FolderPlus className="w-4 h-4" />
                New Folder
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Folder</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="folderName">Folder Name</Label>
                  <Input
                    id="folderName"
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    placeholder="Enter folder name"
                    onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setCreateFolderOpen(false)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleCreateFolder}
                    disabled={!newFolderName.trim() || createFolderMutation.isPending}
                  >
                    {createFolderMutation.isPending ? 'Creating...' : 'Create Folder'}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white">
                <Upload className="w-4 h-4" />
                Upload Images
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload Images</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="folderSelect">Select Folder (Optional)</Label>
                  <select
                    id="folderSelect"
                    className="w-full p-2 border rounded-md"
                    value={selectedFolderId || ''}
                    onChange={(e) => setSelectedFolderId(e.target.value || null)}
                  >
                    <option value="">Root Level</option>
                    {folders.map((folder) => (
                      <option key={folder.id} value={folder.id}>
                        {folder.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="imageUpload">Select Images</Label>
                  <Input
                    id="imageUpload"
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileUpload}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Select one or more image files to upload
                  </p>
                </div>
                {uploadImageMutation.isPending && (
                  <div className="text-sm text-blue-600">
                    Uploading images...
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <FolderBreadcrumb
        currentFolder={currentViewFolder}
        onNavigateToRoot={handleBackToFolders}
      />

      {currentViewFolder ? (
        <FolderView
          folderId={currentViewFolder.id}
          folderName={currentViewFolder.name}
          images={getImagesInFolder(currentViewFolder.id)}
          onDeleteImage={handleDeleteImage}
          onDeleteFolder={() => handleDeleteFolder(currentViewFolder.id)}
        />
      ) : (
        <FoldersGridView
          folders={folders}
          rootImages={getImagesInFolder(null)}
          getFolderImageCount={getFolderImageCount}
          onFolderClick={handleFolderClick}
          onDeleteImage={handleDeleteImage}
          getRandomFolderPreview={getRandomFolderPreview}
        />
      )}

      {folders.length === 0 && getImagesInFolder(null).length === 0 && (
        <EmptyState
          onCreateFolder={() => setCreateFolderOpen(true)}
          onUploadImages={() => setUploadDialogOpen(true)}
        />
      )}
    </div>
  );
};

// Loading skeleton component
const LoadingSkeleton = () => (
  <div className="p-6 space-y-6">
    <div className="flex justify-between items-center">
      <Skeleton className="h-7 w-40" />
      <div className="flex gap-2">
        <Skeleton className="h-10 w-32" />
        <Skeleton className="h-10 w-36" />
      </div>
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="aspect-square" />
      ))}
    </div>
  </div>
);

// Folders grid view component
const FoldersGridView: React.FC<{
  folders: any[];
  rootImages: any[];
  getFolderImageCount: (id: string) => number;
  onFolderClick: (folder: { id: string; name: string }) => void;
  onDeleteImage: (imageId: string, filePath: string) => void;
  getRandomFolderPreview: (folderId: string) => Promise<string | undefined>;
}> = ({
  folders,
  rootImages,
  getFolderImageCount,
  onFolderClick,
  onDeleteImage,
  getRandomFolderPreview,
}) => {
  const [folderPreviews, setFolderPreviews] = useState<Record<string, string>>({});

  React.useEffect(() => {
    const loadPreviews = async () => {
      const previews: Record<string, string> = {};
      for (const folder of folders) {
        try {
          const preview = await getRandomFolderPreview(folder.id);
          if (preview) {
            previews[folder.id] = preview;
          }
        } catch (error) {
          console.error('Error loading preview for folder:', folder.id, error);
        }
      }
      setFolderPreviews(previews);
    };

    if (folders.length > 0) {
      loadPreviews();
    }
  }, [folders, getRandomFolderPreview]);

  return (
    <div className="space-y-8">
      {/* Folders Grid */}
      {folders.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-700 flex items-center gap-2">
            <FolderPlus className="w-4 h-4" />
            Folders ({folders.length})
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {folders.map((folder) => (
              <FolderCard
                key={folder.id}
                folder={folder}
                imageCount={getFolderImageCount(folder.id)}
                previewImage={folderPreviews[folder.id]}
                onClick={() => onFolderClick(folder)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Root Level Images */}
      {rootImages.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium text-gray-700 flex items-center gap-2">
            <ImageIcon className="w-4 h-4" />
            Root Level Images ({rootImages.length})
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {rootImages.map((image) => (
              <ImageCard
                key={image.id}
                image={image}
                onDelete={() => onDeleteImage(image.id, image.file_path)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Folder view component for when viewing inside a folder
const FolderView: React.FC<{
  folderId: string;
  folderName: string;
  images: any[];
  onDeleteImage: (imageId: string, filePath: string) => void;
  onDeleteFolder: () => void;
}> = ({ folderName, images, onDeleteImage, onDeleteFolder }) => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <h4 className="font-medium text-gray-700">{folderName}</h4>
        <Badge variant="secondary">{images.length} images</Badge>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onDeleteFolder}
        className="text-red-600 hover:text-red-700"
      >
        <Trash2 className="w-4 h-4" />
        Delete Folder
      </Button>
    </div>

    {images.length > 0 ? (
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {images.map((image) => (
          <ImageCard
            key={image.id}
            image={image}
            onDelete={() => onDeleteImage(image.id, image.file_path)}
          />
        ))}
      </div>
    ) : (
      <div className="text-center py-12 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
        <ImageIcon className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <h4 className="text-lg font-medium mb-2">No images in this folder</h4>
        <p className="text-sm">Upload images to get started</p>
      </div>
    )}
  </div>
);

// Empty state component
const EmptyState: React.FC<{
  onCreateFolder: () => void;
  onUploadImages: () => void;
}> = ({ onCreateFolder, onUploadImages }) => (
  <div className="text-center py-12 text-gray-500">
    <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
    <h4 className="text-lg font-medium mb-2">No content yet</h4>
    <p className="mb-4">Create folders and upload images to organize your campaign content</p>
    <div className="flex justify-center gap-2">
      <Button
        variant="outline"
        onClick={onCreateFolder}
        className="flex items-center gap-2"
      >
        <FolderPlus className="w-4 h-4" />
        Create Folder
      </Button>
      <Button
        onClick={onUploadImages}
        className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700"
      >
        <Upload className="w-4 h-4" />
        Upload Images
      </Button>
    </div>
  </div>
);

// Enhanced image card component using React Query hook
const ImageCard: React.FC<{
  image: any;
  onDelete: () => void;
}> = ({ image, onDelete }) => {
  const { data: imageUrl, isLoading, error } = useContentImageUrl(image.file_path);

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-2">
        <div className="aspect-square relative group">
          {isLoading ? (
            <div className="w-full h-full bg-gray-200 rounded flex items-center justify-center">
              <LoadingSpinner size="medium" iconClassName="text-gray-400" />
            </div>
          ) : error ? (
            <div className="w-full h-full bg-gray-200 rounded flex flex-col items-center justify-center p-2">
              {error.message?.includes('Authentication required') ? (
                <>
                  <AlertCircle className="w-8 h-8 text-amber-500 mb-2" />
                  <span className="text-amber-700 text-xs text-center">Login required</span>
                </>
              ) : (
                <>
                  <AlertCircle className="w-8 h-8 text-red-500 mb-2" />
                  <span className="text-red-600 text-xs text-center">Failed to load</span>
                </>
              )}
            </div>
          ) : imageUrl ? (
            <ImageWithFallback
              src={imageUrl}
              alt={image.name}
              className="w-full h-full object-cover rounded cursor-pointer"
              onClick={() => window.open(imageUrl, '_blank')}
              fallbackClassName="w-full h-full bg-gray-200 rounded flex items-center justify-center"
              onError={() => console.error('ImageWithFallback onError called for:', image.name)}
            />
          ) : (
            <div className="w-full h-full bg-gray-200 rounded flex flex-col items-center justify-center p-2">
              <ImageIcon className="w-8 h-8 text-gray-400 mb-2" />
              <span className="text-gray-500 text-xs text-center">No URL available</span>
            </div>
          )}

          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
            <div className="flex gap-2">
              {imageUrl && !error && !isLoading && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => window.open(imageUrl, '_blank')}
                >
                  <Eye className="w-3 h-3" />
                </Button>
              )}
              <Button
                size="sm"
                variant="destructive"
                onClick={onDelete}
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-600 mt-1 truncate" title={image.name}>
          {image.name}
        </p>
        {image.file_size && (
          <p className="text-xs text-gray-400">
            {(image.file_size / 1024 / 1024).toFixed(1)} MB
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default ContentGallerySection;
